import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { Film, Clock, Tag, Users, ArrowLeft, Loader2, Share2, Link2 } from 'lucide-react';
import { shareVideo, copyShareLink } from '../utils/share';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtRuntime = (sec) => {
  if (!sec) return null;
  const m = Math.floor(sec / 60), s = Math.round(sec % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

export const FilmDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  const [film, setFilm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axiosInstance.get(`/videos/${id}`)
      .then((r) => setFilm(r.data))
      .catch(() => setError('Film not found'))
      .finally(() => setLoading(false));
  }, [id, axiosInstance]);

  if (loading) return <div className="flex items-center justify-center py-24 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (error || !film) return <div className="text-center py-24 text-zinc-500" data-testid="film-detail-error">{error || 'Not found'}</div>;

  const videoSrc = film.video_path ? `${BACKEND}/api/files/${film.video_path}` : (film.hls_url || null);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="film-detail-page">
      <button onClick={() => navigate('/films')} data-testid="film-back-btn" className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Films
      </button>

      <div className="rounded-2xl overflow-hidden bg-black border border-zinc-800 mb-5">
        {videoSrc ? (
          <video data-testid="film-player" src={videoSrc} controls playsInline className="w-full aspect-video bg-black" poster={film.thumbnail_path ? `${BACKEND}/api/files/${film.thumbnail_path}` : undefined} />
        ) : (
          <div className="w-full aspect-video flex items-center justify-center text-zinc-700"><Film className="w-10 h-10" /></div>
        )}
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">Film</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white" data-testid="film-title">{film.title}</h1>
          <p className="text-sm text-zinc-400 mt-1">by {film.artist}{film.agent_name ? ` · via ${film.agent_name}` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => shareVideo(film)} data-testid="film-share-btn"
            className="flex items-center gap-2 px-4 h-9 rounded-full bg-white text-black text-sm font-semibold hover:bg-zinc-200 transition-colors">
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button type="button" onClick={() => copyShareLink(film)} data-testid="film-copy-link-btn" aria-label="Copy link"
            className="w-9 h-9 rounded-full bg-zinc-800 text-white flex items-center justify-center hover:bg-zinc-700 transition-colors">
            <Link2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mt-4 text-sm text-zinc-400">
        {film.genre && <span className="flex items-center gap-1.5" data-testid="film-genre"><Tag className="w-4 h-4" /> {film.genre}</span>}
        {fmtRuntime(film.runtime || film.duration) && <span className="flex items-center gap-1.5" data-testid="film-runtime"><Clock className="w-4 h-4" /> {fmtRuntime(film.runtime || film.duration)}</span>}
      </div>

      {film.synopsis && (
        <div className="mt-6" data-testid="film-synopsis">
          <h2 className="text-base font-semibold text-white mb-2">Synopsis</h2>
          <p className="text-sm text-zinc-300 leading-relaxed">{film.synopsis}</p>
        </div>
      )}

      {Array.isArray(film.credits) && film.credits.length > 0 && (
        <div className="mt-6" data-testid="film-credits">
          <h2 className="text-base font-semibold text-white mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> Credits</h2>
          <ul className="space-y-1">
            {film.credits.map((c, i) => (
              <li key={`${c.role}-${c.name}`} className="text-sm text-zinc-300"><span className="text-zinc-500">{c.role}:</span> {c.name}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FilmDetailPage;
